import React from 'react';
import { Text, Typography } from '../common/Typography';
import { StatusBadge } from '../common';
import { format } from 'date-fns';

/**
 * Visual timeline showing tournament progression
 * For member/player view to understand tournament status
 */
const TournamentTimeline = ({ tournament, matches = [] }) => {
  const status = tournament?.status || 'draft';
  const hasGroupMatches = matches.some(m => m.round === 'group');
  const hasKnockoutMatches = matches.some(m => m.round !== 'group');
  
  const stages = [
    {
      id: 'setup',
      title: 'Setup',
      icon: '📋',
      description: 'Tournament created',
      status: 'complete',
      date: tournament?.created_at
    },
    {
      id: 'registration',
      title: 'Registration',
      icon: '✍️',
      description: status === 'draft' ? 'Teams being added' : 'Registration closed',
      status: status === 'draft' ? 'current' : 'complete',
      date: null
    },
    {
      id: 'groups',
      title: 'Group Stage',
      icon: '⚔️',
      description: !hasGroupMatches 
        ? 'Not started yet' 
        : status === 'group_stage' 
          ? 'Matches in progress' 
          : 'Group stage complete',
      status: !hasGroupMatches 
        ? 'future' 
        : status === 'group_stage' 
          ? 'current' 
          : 'complete',
      date: null
    },
    {
      id: 'knockout',
      title: 'Knockout',
      icon: '🏆',
      description: !hasKnockoutMatches 
        ? 'Awaiting group completion' 
        : status === 'knockout' 
          ? 'Bracket in progress' 
          : status === 'completed'
            ? 'Tournament finished'
            : 'Not started yet',
      status: !hasKnockoutMatches 
        ? 'future' 
        : status === 'knockout' 
          ? 'current' 
          : status === 'completed'
            ? 'complete'
            : 'future',
      date: null
    },
    {
      id: 'complete',
      title: status === 'completed' ? 'Completed' : status === 'cancelled' ? 'Cancelled' : 'Finals',
      icon: status === 'completed' ? '🎉' : status === 'cancelled' ? '❌' : '👑',
      description: status === 'completed' 
        ? 'Champions crowned!' 
        : status === 'cancelled' 
          ? 'Tournament cancelled' 
          : 'Championship awaits',
      status: status === 'completed' || status === 'cancelled' ? 'complete' : 'future',
      date: status === 'completed' ? tournament?.updated_at : null
    }
  ];

  return (
    <div className="relative">
      {/* Desktop: Horizontal timeline */}
      <div className="hidden md:block">
        <div className="flex items-start justify-between relative">
          {/* Progress line */}
          <div className="absolute top-6 left-0 right-0 h-1 bg-border-medium" style={{ zIndex: 0 }} />
          <div 
            className="absolute top-6 left-0 h-1 bg-primary transition-all duration-500" 
            style={{ 
              width: `${(stages.filter(s => s.status === 'complete').length / stages.length) * 100}%`,
              zIndex: 0 
            }} 
          />

          {stages.map((stage, index) => {
            const isComplete = stage.status === 'complete';
            const isCurrent = stage.status === 'current';
            const isFuture = stage.status === 'future';

            return (
              <div key={stage.id} className="flex flex-col items-center relative" style={{ flex: 1, zIndex: 1 }}>
                {/* Icon circle */}
                <div
                  className={`
                    flex items-center justify-center w-12 h-12 rounded-full text-2xl
                    transition-all duration-300 shadow-md
                    ${isComplete 
                      ? 'bg-success text-white' 
                      : isCurrent 
                        ? 'bg-primary text-white animate-pulse ring-4 ring-primary/20' 
                        : 'bg-surface-subtle border-2 border-border-medium'
                    }
                  `}
                >
                  {stage.icon}
                </div>

                {/* Title and description */}
                <div className="mt-3 text-center max-w-[120px]">
                  <Typography 
                    variant="strong" 
                    className={`text-sm mb-1 block ${isCurrent ? 'text-primary' : isComplete ? 'text-success' : 'text-text-muted'}`}
                  >
                    {stage.title}
                  </Typography>
                  <Text className="text-xs text-text-muted leading-tight">
                    {stage.description}
                  </Text>
                  {stage.date && (
                    <Text className="text-xs text-text-muted mt-1">
                      {format(new Date(stage.date), 'MMM d')}
                    </Text>
                  )}
                </div>

                {isCurrent && (
                  <StatusBadge variant="primary" size="small" className="mt-2">
                    Current
                  </StatusBadge>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Mobile: Vertical timeline */}
      <div className="md:hidden space-y-4">
        {stages.map((stage, index) => {
          const isComplete = stage.status === 'complete';
          const isCurrent = stage.status === 'current';
          const isFuture = stage.status === 'future';

          return (
            <div key={stage.id} className="flex items-start">
              {/* Icon and connector */}
              <div className="flex flex-col items-center shrink-0">
                <div
                  className={`
                    flex items-center justify-center w-10 h-10 rounded-full text-xl
                    transition-all duration-300
                    ${isComplete 
                      ? 'bg-success text-white shadow-md' 
                      : isCurrent 
                        ? 'bg-primary text-white animate-pulse ring-4 ring-primary/20' 
                        : 'bg-surface-subtle border-2 border-border-medium'
                    }
                  `}
                >
                  {stage.icon}
                </div>
                {index < stages.length - 1 && (
                  <div 
                    className={`w-0.5 h-12 mt-2 ${isComplete ? 'bg-success' : 'bg-border-medium'}`} 
                  />
                )}
              </div>

              {/* Content */}
              <div className="ml-4 flex-1 pb-4">
                <div className="flex items-center gap-2 mb-1">
                  <Typography 
                    variant="strong" 
                    className={`text-sm ${isCurrent ? 'text-primary' : isComplete ? 'text-success' : 'text-text-muted'}`}
                  >
                    {stage.title}
                  </Typography>
                  {isCurrent && (
                    <StatusBadge variant="primary" size="small">
                      Now
                    </StatusBadge>
                  )}
                </div>
                <Text className="text-sm text-text-secondary">
                  {stage.description}
                </Text>
                {stage.date && (
                  <Text className="text-xs text-text-muted mt-1">
                    {format(new Date(stage.date), 'MMM d, yyyy')}
                  </Text>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default TournamentTimeline;
